/**
 * Trace Export Extension for Inspector v2
 *
 * Adds Trace-specific export tools to the Inspector's Tools pane.
 */

import React, { useState } from 'react';
import {
    ToolsServiceIdentity,
    type ServiceDefinition,
    type IToolsService,
} from '@babylonjs/inspector';
import type { Scene } from '@babylonjs/core/scene';
import type { TraceViewerElement } from 'trace-viewer';
import type { ZipProgress } from 'trace-viewer/scene';
import { buildPublishSummary, type CmsManager, type PublishResult, type RootContentFormat } from './cms/CmsManager';
import { showToast } from './toast';
import { downloadBlob } from './utils/download-blob';
import { BUNDLE_EXTENSION } from './scene-formats';
import { exportWithProgressToast } from './utils/export-toast';
import { editorExclusion, exportGlz } from './editor-content';

// ============================================================================
// CMS context (set by index.ts when in CMS mode)
// ============================================================================

let _cmsManager: CmsManager | null = null;

export function setExportCmsManager(manager: CmsManager | null): void {
    _cmsManager = manager;
}

// ============================================================================
// Export Tools UI Components
// ============================================================================

/**
 * Get the trace-viewer element
 */
const getViewer = (): TraceViewerElement | null => {
    return document.querySelector('trace-viewer');
};

/** Run a whole-scene archive export behind the shared progress toast. */
const runArchiveExport = async (
    ext: string,
    exportFn: (viewer: TraceViewerElement, onProgress: (p: ZipProgress) => void) => Promise<Blob>,
) => {
    const viewer = getViewer();
    if (!viewer) {
        console.error('[Trace Export] No trace-viewer element found');
        return;
    }

    const fileName = `${viewer.traceScene?.sceneName ?? 'scene'}${ext}`;
    await exportWithProgressToast(fileName, (onProgress) => exportFn(viewer, onProgress));
};

/**
 * Export the entire scene as a downloaded `.glz` bundle (CONTENT_MODEL_SPEC
 * §4), with the editor folder unless the Export menu's choice is off.
 */
const exportSceneAsGlz = () =>
    runArchiveExport(BUNDLE_EXTENSION, (viewer, onProgress) => exportGlz(viewer, onProgress));

/**
 * Export the entire scene as a downloaded glTF 2.1 JSON document.
 * Standalone: no folder travels with it, so every asset reference must
 * already resolve to an external URL — local-only assets surface as a toast
 * so the user can fix them or pick a different format.
 */
const exportSceneAsGltf = async () => {
    const viewer = getViewer();
    if (!viewer) {
        console.error('[Trace Export] No trace-viewer element found');
        return;
    }

    try {
        console.log('[Trace Export] Exporting scene as .gltf...');
        const blob = await viewer.exportSceneToGltfBlob(editorExclusion(viewer.traceScene));
        downloadBlob(blob, `${viewer.traceScene?.sceneName ?? 'scene'}.gltf`);
        console.log('[Trace Export] Scene .gltf export complete');
    } catch (error) {
        console.error('[Trace Export] Failed to export scene as .gltf:', error);
        const msg = error instanceof Error ? error.message : String(error);
        showToast(`glTF export failed: ${msg}`, 'error');
    }
};

/**
 * Publish scene to CMS. Returns the per-surface result so the caller can
 * toast what actually happened (published / already up to date / failures).
 * `rootFormat` selects the RootContent encoding — `.gltf` (the folder
 * document, CONTENT_MODEL_SPEC §2.1) or `.zip` (legacy bundle). Both run
 * through the same Scene validators.
 */
const publishScene = async (rootFormat: RootContentFormat): Promise<PublishResult> => {
    const viewer = getViewer();
    if (!viewer || !_cmsManager) {
        throw new Error('No CMS connection');
    }

    return _cmsManager.publishScene(viewer, { rootFormat });
};

// ============================================================================
// Export Section Content Component
// ============================================================================

/**
 * Button style matching Inspector v2 aesthetics
 */
const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '8px 12px',
    marginBottom: '8px',
    backgroundColor: 'var(--colorNeutralBackground3)',
    border: '1px solid var(--colorNeutralStroke1)',
    borderRadius: '4px',
    color: 'var(--colorNeutralForeground1)',
    cursor: 'pointer',
    fontSize: '13px',
    textAlign: 'left' as const,
};

/**
 * Icon for scene export (download arrow)
 */
const SceneExportIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 1a.5.5 0 0 1 .5.5v5.793l2.146-2.147a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 1 1 .708-.708L7.5 7.293V1.5A.5.5 0 0 1 8 1z"/>
        <path d="M2.5 10a.5.5 0 0 1 .5.5V13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0V13a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
    </svg>
);

/**
 * Icon for publish (cloud upload)
 */
const PublishIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M4.406 1.342A5.53 5.53 0 0 1 8 0c2.69 0 4.923 2 5.166 4.579C14.758 4.804 16 6.137 16 7.773 16 9.569 14.502 11 12.687 11H10a.5.5 0 0 1 0-1h2.688C13.979 10 15 8.988 15 7.773c0-1.216-1.02-2.228-2.313-2.228h-.5v-.5C12.188 2.825 10.328 1 8 1a4.53 4.53 0 0 0-2.941 1.1c-.757.652-1.153 1.438-1.153 2.055v.448l-.445.049C2.064 4.805 1 5.952 1 7.318 1 8.785 2.23 10 3.781 10H6a.5.5 0 0 1 0 1H3.781C1.708 11 0 9.366 0 7.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383z"/>
        <path d="M7.646 4.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 5.707V14.5a.5.5 0 0 1-1 0V5.707L5.354 7.854a.5.5 0 1 1-.708-.708l3-3z"/>
    </svg>
);

/**
 * Trace Export Section Content
 * Renders export buttons in the Tools pane
 */
const TraceExportContent: React.FC<{ context: Scene }> = () => {
    // Track which format is currently uploading (if any) rather than a
    // free-form status string — lets the two buttons drive their labels
    // off a typed enum instead of string-matching against the message.
    const [publishingFormat, setPublishingFormat] = useState<RootContentFormat | null>(null);
    const isCmsMode = _cmsManager !== null;

    const handlePublish = async (rootFormat: RootContentFormat) => {
        setPublishingFormat(rootFormat);
        try {
            const result = await publishScene(rootFormat);
            showToast(buildPublishSummary(result), result.failures.length > 0 ? 'error' : 'success');
        } catch (error) {
            console.error('[Trace Export] Publish failed:', error);
            const msg = error instanceof Error ? error.message : String(error);
            showToast(`Publish failed: ${msg}`, 'error');
        } finally {
            setPublishingFormat(null);
        }
    };

    const publishLabel = (format: RootContentFormat): string =>
        publishingFormat === format ? `Publishing .${format}…` : `Publish as .${format}`;

    return (
        <div style={{ padding: '8px 0' }}>
            <button
                style={buttonStyle}
                onClick={() => exportSceneAsGlz()}
                onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--colorNeutralBackground3Hover)';
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--colorNeutralBackground3)';
                }}
            >
                <SceneExportIcon />
                <span>Download .glz</span>
            </button>

            <button
                style={buttonStyle}
                onClick={() => exportSceneAsGltf()}
                onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--colorNeutralBackground3Hover)';
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--colorNeutralBackground3)';
                }}
            >
                <SceneExportIcon />
                <span>Download .gltf</span>
            </button>

            {isCmsMode && (
                <>
                    <button
                        style={buttonStyle}
                        onClick={() => handlePublish('zip')}
                        disabled={publishingFormat !== null}
                        onMouseOver={(e) => {
                            if (publishingFormat === null) e.currentTarget.style.backgroundColor = 'var(--colorNeutralBackground3Hover)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--colorNeutralBackground3)';
                        }}
                    >
                        <PublishIcon />
                        <span>{publishLabel('zip')}</span>
                    </button>
                    <button
                        style={buttonStyle}
                        onClick={() => handlePublish('gltf')}
                        disabled={publishingFormat !== null}
                        onMouseOver={(e) => {
                            if (publishingFormat === null) e.currentTarget.style.backgroundColor = 'var(--colorNeutralBackground3Hover)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--colorNeutralBackground3)';
                        }}
                    >
                        <PublishIcon />
                        <span>{publishLabel('gltf')}</span>
                    </button>
                </>
            )}
        </div>
    );
};

// ============================================================================
// Export Service Definition
// ============================================================================

/**
 * Trace Export Service Definition
 * Adds an "Export" section to the Tools pane with Trace-specific export options
 */
export const TraceExportServiceDefinition: ServiceDefinition<[], [IToolsService]> = {
    friendlyName: 'Trace Export Tools',
    consumes: [ToolsServiceIdentity],

    factory: (toolsService: IToolsService) => {
        console.log('[Trace Export] Initializing export tools...');

        // Add a new "Trace Export" section to the Tools pane
        const sectionDisposable = toolsService.addSection({
            identity: 'Trace',
            order: 10, // Position after default sections
            collapseByDefault: false,
        });

        // Add the export buttons content to our section
        const contentDisposable = toolsService.addSectionContent({
            key: 'trace-export-content',
            section: 'Trace',
            order: 0,
            component: TraceExportContent,
        });

        console.log('[Trace Export] Export section added to Tools pane');

        return {
            dispose: () => {
                console.log('[Trace Export] Disposing export tools');
                contentDisposable.dispose();
                sectionDisposable.dispose();
            }
        };
    }
};

